import { DateTime } from 'luxon';
import { makeAutoObservable } from 'mobx';
import { LoanApiResponse, LoansApi } from '../../api/Loans';
import { Group } from '../models/Group';
import { Loan, LoanSummary } from '../models/Loan';
import { BalancerSettings } from '../models/BalancerSettings';

export class LoansStore {
  loans: Map<string, Loan> = new Map();
  groupLoans: Map<number, string[]> = new Map();
  groupLoanConfigurations: Map<number, BalancerSettings> = new Map();
  stale: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  loansKey = (groupId: number, loanId: number) => {
    return `${groupId}:${loanId}`;
  };

  getGroupLoans = (group: Group, date: DateTime): LoanSummary[] => {
    if (this.groupLoans.has(group.id)) {
      return this.groupLoans
        .get(group.id)
        .map(loanKey => this.loans.get(loanKey).getSummary(date))
        .filter(summary => summary != null && summary != undefined);
    }
    return [];
  };

  getGroupConfiguration = (groupId: number): BalancerSettings => {
    return this.groupLoanConfigurations.get(groupId);
  };

  setGroupConfiguration = (
    groupId: number,
    configuration: BalancerSettings
  ): void => {
    this.groupLoanConfigurations.set(groupId, configuration);
  };

  getLoan = (groupId: number, loanId: number): Loan => {
    const key = this.loansKey(groupId, loanId);
    return this.loans.has(key) ? this.loans.get(key) : null;
  };

  getLoanEarliestTransactionDate = (
    groupId: number,
    loanId: number
  ): Date | null => {
    const key = this.loansKey(groupId, loanId);
    const loan = this.loans.get(key);

    if (!loan || loan.transactions.length == 0) {
      return null;
    }

    return loan.transactions
      .reduce((earliest, current) => {
        return current.date < earliest.date ? current : earliest;
      })
      .date.toJSDate();
  };

  loadGroupLoans = async (
    group: Group,
    date: DateTime,
    force: boolean = false
  ): Promise<LoanSummary[]> => {
    const response = await this.loadGroupLoansAtDate(group, date, force);
    this.updatePostings(group, date);
    return response;
  };

  private loadGroupLoansAtDate = (
    group: Group,
    date: DateTime,
    force: boolean = false
  ): Promise<LoanSummary[]> => {
    const existing = this.getGroupLoans(group, date);
    if (existing.length > 0 && !force) {
      return Promise.resolve(existing);
    }
    this.groupLoanConfigurations.set(group.id, group.balancerSettings);
    return LoansApi.getGroupLoans(group.id, date).then(
      (loans: LoanApiResponse[]) => {
        const loanKeys = loans.map(apiLoan => {
          const key = this.loansKey(group.id, apiLoan.id);

          if (this.loans.has(key)) {
            this.loans.get(key).parseApi(apiLoan, date);
          } else {
            const loan = Loan.fromApi(group, apiLoan, date);
            this.loans.set(key, loan);
          }

          return key;
        });
        this.groupLoans.set(group.id, loanKeys);

        return this.getGroupLoans(group, date);
      }
    );
  };

  private updatePostings = async (
    group: Group,
    date: DateTime
  ): Promise<void> => {
    for (const summary of this.getGroupLoans(group, date)) {
      await this.updateFxPostingIfNecessary(group, summary.loan, date);
      await this.updateInterestPostingIfNecessary(group, summary.loan, date);
    }
  };

  /**
   * The FX Posting is outdated if:
   * - It's in the future relative to the date we're looking at, and the error on the
   *   date it was posted is no longer 0
   * - It's in the past, and the current error is not 0
   */
  updateFxPostingIfNecessary = async (
    group: Group,
    loan: Loan,
    date: DateTime
  ) => {
    let summaryAtDate = loan.getSummary(date);

    if (summaryAtDate.org2.hasFxPostingForMonth(date)) {
      const posting = summaryAtDate.org2.lastFxPosting;

      if (posting.issueDate > date) {
        await this.loadGroupLoansAtDate(group, posting.issueDate, false);
        summaryAtDate = loan.getSummary(posting.issueDate);
      }

      if (summaryAtDate?.errorBorrowerCurrency !== 0) {
        posting.isOutdated = true;
      }
    }
  };

  /**
   * An interest posting is outdated if the interest figure has changed for that month
   */
  updateInterestPostingIfNecessary = async (
    group: Group,
    loan: Loan,
    date: DateTime
  ) => {
    if (loan.getSummary(date).org1.hasInterestPostingForMonth(date)) {
      const invoice = loan.getSummary(date).org1.lastInterestPosting;
      const bill = loan.getSummary(date).org2.lastInterestPosting;

      if (!loan.hasSummary(invoice.issueDate)) {
        await this.loadGroupLoansAtDate(group, invoice.issueDate, false);
      }

      const summary = loan.getSummary(invoice.issueDate);

      if (invoice.lines[0].amount !== summary?.lenderInterest) {
        invoice.isOutdated = true;
        bill.isOutdated = true;
      }
    }
  };
}
